import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ImageEditorComponent } from '@catch/image-editor';
import { ConstantsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, tap } from 'rxjs';
import { PlatformAvatarPlaceholderComponent } from '../../../../components/avatar-placeholder/platform-avatar-placeholder.component';
import { PlatformLocationsService } from '../../../../services/platform-locations.service';

@Component({
  selector: 'app-platform-profile-contact',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    ImageEditorComponent,
    NgSelectModule,
    PlatformAvatarPlaceholderComponent,
  ],
  templateUrl: './platform-profile-contact.component.html',
  styleUrl: './platform-profile-contact.component.scss',
})
export class PlatformProfileContactComponent implements OnInit {
  private readonly locations = inject(PlatformLocationsService);
  private readonly destroyRef = inject(DestroyRef);
  readonly constantsService = inject(ConstantsService);

  readonly form = input.required<FormGroup>();
  readonly photoNonce = input(0);
  readonly submitAttempt = input(false);

  private readonly nameSnapshot = signal({ firstName: '', lastName: '' });
  private readonly photoSrcValue = signal<string | null>(null);
  private readonly citySearchResults = signal<string[]>([]);
  /** Form city value — kept in items so typeahead never drops the selection. */
  private readonly selectedCity = signal('');
  /** Kept separately so typeahead results never wipe the selected label. */
  readonly cityLabel = signal('');
  readonly photoSrc = computed(() => this.photoSrcValue());
  readonly photoInitials = computed(() => {
    const { firstName, lastName } = this.nameSnapshot();
    const letters = `${firstName.charAt(0)}${lastName.charAt(0)}`.trim();
    return letters || undefined;
  });
  readonly cityItems = computed((): string[] => {
    const current = this.selectedCity().trim();
    const results = this.citySearchResults();
    if (!current) {
      return results;
    }
    if (results.includes(current)) {
      return results;
    }
    return [current, ...results];
  });
  readonly citiesLoading = signal(false);
  readonly cityInput$ = new Subject<string>();

  constructor() {
    effect((onCleanup) => {
      const form = this.form();
      this.syncFormSnapshot(form);
      const sub = form.valueChanges.subscribe(() => this.syncFormSnapshot(form));
      const citySub = form.controls['city'].valueChanges.subscribe((value) => {
        this.setCityLabel(value);
      });
      onCleanup(() => {
        sub.unsubscribe();
        citySub.unsubscribe();
      });
    });
  }

  ngOnInit(): void {
    this.cityInput$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => this.citiesLoading.set(true)),
        switchMap((term) => {
          const query = term?.trim() ?? '';
          if (query.length < 2) {
            this.citiesLoading.set(false);
            // Keep last results so the selected city stays visible in items.
            return of(this.citySearchResults());
          }
          return this.locations.searchCities(query).pipe(
            catchError(() => of([] as string[])),
            tap(() => this.citiesLoading.set(false))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((cities) => this.citySearchResults.set(cities));
  }

  /**
   * Stores the cropped photo as a data URL.
   * @param image - Cropped image as base64
   */
  onPhotoConfirm(image: string): void {
    this.form().controls['photoSrc'].setValue(image);
    this.form().controls['photoSrc'].markAsDirty();
  }

  /**
   * Removes the photo from the form.
   */
  onPhotoDelete(): void {
    this.form().controls['photoSrc'].setValue(null);
    this.form().controls['photoSrc'].markAsDirty();
  }

  /**
   * Keeps the visible city label in sync with the selected option.
   * @param city - Selected city name or null when cleared
   */
  onCityChange(city: string | null): void {
    this.setCityLabel(city);
  }

  /**
   * Clears the city selection and search results.
   */
  onCityClear(): void {
    this.setCityLabel('');
    this.citySearchResults.set([]);
  }

  /**
   * Syncs name, photo and city label from the parent form.
   * @param form - Contact form group
   */
  private syncFormSnapshot(form: FormGroup): void {
    this.nameSnapshot.set({
      firstName: ((form.controls['firstName'].value as string) ?? '').trim(),
      lastName: ((form.controls['lastName'].value as string) ?? '').trim(),
    });
    this.photoSrcValue.set(
      (form.controls['photoSrc'].value as string | null) ?? null
    );
    this.setCityLabel(form.controls['city'].value);
  }

  /**
   * Updates the selected-city signals used for items and the label template.
   * @param value - City name from the form or select
   */
  private setCityLabel(value: unknown): void {
    const city = typeof value === 'string' ? value.trim() : '';
    this.selectedCity.set(city);
    this.cityLabel.set(city);
  }
}
